import React, { useEffect, useState } from "react";
import { HardDrive, Loader2, Info } from "lucide-react";
import { MKT_GOLD, MKT_TINT } from "../../lib/marketplace";
import { apiUso, bytesLegible } from "../../lib/marketplaceApi";

const SCOPE_LABEL = {
  logo: "Logo",
  portada: "Portada de tienda",
  product_cover: "Portadas de productos",
  product_gallery: "Galerías de productos",
  product_file: "Archivos de productos",
  course_cover: "Portadas de cursos",
  course_gallery: "Imágenes de cursos",
  course_doc: "Documentos de cursos",
  course_video: "Videos de cursos",
  lesson_material: "Material de lecciones",
  otros: "Otros",
};

export default function AlmacenamientoWidget() {
  const [uso, setUso] = useState(undefined);

  useEffect(() => { apiUso().then(setUso).catch(() => setUso(null)); }, []);

  if (uso === undefined) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-soft p-5 flex items-center gap-2 text-slate-500" data-testid="almacenamiento-loading">
        <Loader2 className="animate-spin" size={16} /> Calculando almacenamiento…
      </div>
    );
  }
  if (!uso) return null;

  const pct = Math.min(100, uso.porcentaje || 0);
  const alto = pct >= 90;
  const barColor = alto ? "#DC2626" : pct >= 70 ? "#D97706" : MKT_GOLD;
  const scopes = Object.entries(uso.por_scope || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-soft p-5" data-testid="almacenamiento-widget">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }}>
            <HardDrive size={18} />
          </span>
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">Almacenamiento de la tienda</h3>
            <p className="text-xs text-slate-500">{uso.archivos} archivo(s) · cuota total {uso.limites?.tienda_gb} GB</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-extrabold" style={{ color: barColor }} data-testid="almacenamiento-usado">
            {bytesLegible(uso.usado)} <span className="text-slate-400 font-semibold">/ {bytesLegible(uso.cuota)}</span>
          </p>
          <p className="text-xs text-slate-500" data-testid="almacenamiento-disponible">Disponible: {bytesLegible(uso.disponible)}</p>
        </div>
      </div>

      <div className="mt-4 h-3 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} data-testid="almacenamiento-barra" />
      </div>

      {alto && (
        <p className="mt-2 text-xs text-red-600 flex items-center gap-1" data-testid="almacenamiento-alerta">
          <Info size={12} /> Estás cerca del límite. Elimina archivos que no uses para liberar espacio.
        </p>
      )}

      {scopes.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="almacenamiento-desglose">
          {scopes.map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[11px] font-semibold text-slate-500 truncate">{SCOPE_LABEL[k] || k}</p>
              <p className="text-sm font-bold text-slate-800">{bytesLegible(v)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
        <span>Producto: hasta {uso.limites?.producto_mb} MB ({uso.limites?.max_galeria_producto} imágenes, archivo {uso.limites?.producto_archivo_mb} MB)</span>
        <span>Curso: hasta {uso.limites?.curso_mb} MB en documentos ({uso.limites?.max_docs_curso} docs · {uso.limites?.curso_doc_mb} MB c/u)</span>
        <span>Videos: solo visualización, no descargables</span>
      </div>
    </section>
  );
}
